import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { 
  BookOpen, Target, Trophy, MapPin, Star, 
  ChevronRight, Check, Sparkles, Users, Calendar,
  Layers, FileText, TrendingUp, Flame, Clock, Award,
  ShieldCheck, Zap, ArrowRight, CheckCircle2, XCircle,
  MessageCircle, Phone, Mail, GraduationCap, Globe, ChevronDown
} from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import SEO from "../components/SEO";

const LandingPage = ({ auth }) => {
  const { language, setLanguage, t } = useLanguage();
  
  const features = [
    {
      icon: <Target className="w-6 h-6" />,
      title: t("landing.featuresSection.feature1") || "500+ Practice Questions",
      description: t("landing.featuresSection.feature1Desc") || "Comprehensive question bank covering all 11 chapters with detailed explanations.",
      link: "/features/chapter-practice"
    },
    {
      icon: <Layers className="w-6 h-6" />,
      title: t("landing.featuresSection.feature2") || "200+ Flashcards",
      description: t("landing.featuresSection.feature2Desc") || "Master key facts, dates, and personalities with interactive flashcards.",
      link: "/features/flashcards"
    },
    {
      icon: <Trophy className="w-6 h-6" />,
      title: t("landing.featuresSection.feature3") || "Real Test Simulator",
      description: t("landing.featuresSection.feature3Desc") || "Experience the exact Government of Canada test interface before exam day.",
      link: "/features/simulations"
    },
    {
      icon: <Award className="w-6 h-6" />,
      title: t("landing.featuresSection.feature4") || "Official Mock Tests",
      description: t("landing.featuresSection.feature4Desc") || "3 comprehensive mock exams that mirror the real citizenship test format.",
      link: "/features/mock-tests"
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: t("landing.featuresSection.feature5") || "Analytics & Insights",
      description: t("landing.featuresSection.feature5Desc") || "Smart analysis identifies which chapters need more focus based on your test results.",
      link: "/features/analytics"
    },
    {
      icon: <FileText className="w-6 h-6" />,
      title: t("landing.featuresSection.feature6") || "Important Compilations",
      description: t("landing.featuresSection.feature6Desc") || "All Important Dates, Key Personalities, and summaries in one place.",
      link: null
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: t("landing.featuresSection.feature7") || "Journey Tracker",
      description: t("landing.featuresSection.feature7Desc") || "Understand every stage from application to oath ceremony.",
      link: null
    },
    {
      icon: <Flame className="w-6 h-6" />,
      title: t("landing.featuresSection.feature8") || "Study Streaks",
      description: t("landing.featuresSection.feature8Desc") || "Build consistent habits with daily streaks and achievements.",
      link: null
    }
  ];

  const testimonials = [
    {
      name: "Priya S.",
      location: "Toronto, ON",
      avatar: "PS",
      text: "I passed with 20/20! The practice questions were identical to my real test. MaplePass made all the difference.",
      score: "20/20",
      highlight: "Passed with 20/20"
    },
    {
      name: "Ahmed H.",
      location: "Vancouver, BC",
      avatar: "AH",
      text: "The study planner helped me prepare in just 10 days. The weak area detection was incredibly helpful!",
      score: "19/20",
      highlight: "10-day prep success"
    },
    {
      name: "Maria S.",
      location: "Calgary, AB",
      avatar: "MS",
      text: "As someone with English as a second language, the clear explanations and flashcards made learning so much easier.",
      score: "18/20",
      highlight: "ESL-friendly"
    },
    {
      name: "Wei C.",
      location: "Montreal, QC",
      avatar: "WC",
      text: "I failed my first attempt using just the book. MaplePass helped me pass on my second try with flying colors!",
      score: "19/20",
      highlight: "Second try success"
    },
    {
      name: "Oluwaseun A.",
      location: "Edmonton, AB",
      avatar: "OA",
      text: "The mock tests were so realistic. When I sat for the real test, I felt like I had done it before!",
      score: "20/20",
      highlight: "Realistic mock tests"
    },
    {
      name: "Fatima A.",
      location: "Ottawa, ON",
      avatar: "FA",
      text: "I loved the flashcards for Important Dates. They made memorizing so much easier. Highly recommend!",
      score: "18/20",
      highlight: "Great flashcards"
    }
  ];

  const stats = [
    { number: "30+", label: "Progressive Simulations" },
    { number: "500+", label: "Test Questions" },
    { number: "11", label: "Chapters Covered" },
    { number: "200+", label: "Flashcards" }
  ];

  const comparisonFeatures = [
    { feature: language === "fr" ? "Questions pratiques" : "Practice Questions", book: language === "fr" ? "~50 exemples" : "~50 sample", mapplepass: "500+ questions" },
    { feature: language === "fr" ? "Simulations de test" : "Test Simulations", book: language === "fr" ? "Aucune" : "None", mapplepass: "33 simulations" },
    { feature: language === "fr" ? "Cartes mémoire" : "Flashcards", book: language === "fr" ? "Aucune" : "None", mapplepass: language === "fr" ? "200+ cartes" : "200+ cards" },
    { feature: language === "fr" ? "Suivi de progression" : "Progress Tracking", book: language === "fr" ? "Manuel" : "Manual", mapplepass: language === "fr" ? "Automatique" : "Automatic" },
    { feature: language === "fr" ? "Détection des lacunes" : "Weak Area Detection", book: language === "fr" ? "Aucune" : "None", mapplepass: language === "fr" ? "Intelligent et personnalisé" : "Smart & personalized" },
    { feature: language === "fr" ? "Horaire d'études" : "Study Schedule", book: language === "fr" ? "À faire soi-même" : "DIY", mapplepass: language === "fr" ? "Personnalisé" : "Personalized" },
  ];

  const howItWorks = [
    {
      step: "1",
      title: t("landing.howItWorksSection.step1Title") || "Start with the Study Guide",
      description: t("landing.howItWorksSection.step1Desc") || "Learn chapter by chapter with our comprehensive guide covering all 11 chapters of Discover Canada.",
      icon: <BookOpen className="w-6 h-6" />
    },
    {
      step: "2",
      title: t("landing.howItWorksSection.step2Title") || "Practice with Questions",
      description: t("landing.howItWorksSection.step2Desc") || "Test your knowledge with 500+ chapter-wise practice questions and instant feedback.",
      icon: <Target className="w-6 h-6" />
    },
    {
      step: "3",
      title: t("landing.howItWorksSection.step3Title") || "Take Simulation Tests",
      description: t("landing.howItWorksSection.step3Desc") || "Experience the real test format with our Government of Canada-style simulator.",
      icon: <Zap className="w-6 h-6" />
    },
    {
      step: "4",
      title: t("landing.howItWorksSection.step4Title") || "Pass Your Exam",
      description: t("landing.howItWorksSection.step4Desc") || "Walk into your test confident and prepared. Our comprehensive practice tools help you walk in prepared and confident.",
      icon: <Trophy className="w-6 h-6" />
    }
  ];

  const faqs = language === "fr" ? [
    {
      question: "Je n'ai que quelques jours avant mon examen — est-ce que ça va m'aider?",
      answer: "Absolument! Notre planificateur d'études intelligent crée un programme intensif 'cours accéléré' pour les délais courts. De nombreux utilisateurs ont réussi avec seulement 3 à 5 jours de préparation avec MaplePass."
    },
    {
      question: "En quoi est-ce différent de simplement lire Découvrir le Canada?",
      answer: "Le livre seul a un taux d'échec élevé car il est dense et difficile à retenir. MaplePass transforme le contenu en questions interactives, cartes mémoire et simulations qui vous aident à vraiment mémoriser ce que vous étudiez."
    },
    {
      question: "J'ai échoué à mon premier examen. MaplePass peut-il m'aider à réussir?",
      answer: "Oui! Beaucoup de nos utilisateurs sont venus chez nous après avoir échoué à leur première tentative. Notre détection des points faibles identifie exactement où vous avez eu des difficultés, et nos tests pratiques garantissent que vous êtes pleinement préparé pour votre prochaine tentative."
    },
    {
      question: "Les questions sont-elles similaires au vrai examen?",
      answer: "Nos questions sont méticuleusement compilées à partir d'une large gamme de sources, y compris des documents de bibliothèques publiques et des publications gouvernementales officielles. Nous intégrons également les commentaires des utilisateurs pour nous assurer que notre banque reste à jour avec ce qui est demandé dans l'examen réel."
    },
    {
      question: "Qu'est-ce qui est inclus dans la garantie de réussite?",
      answer: "Si vous complétez tous les tests pratiques, simulations et examens blancs sur MaplePass mais ne réussissez toujours pas votre examen de citoyenneté, nous vous rembourserons intégralement. Nous avons cette confiance en notre programme."
    }
  ] : [
    {
      question: "I only have a few days before my test — will this help?",
      answer: "Absolutely! Our Smart Study Planner creates an intensive 'crash course' schedule for short timeframes. Many users have passed with just 3-5 days of preparation using MaplePass."
    },
    {
      question: "How is this different from just reading Discover Canada?",
      answer: "The book alone has a high failure rate because it's dense and hard to retain. MaplePass transforms the content into interactive questions, flashcards, and simulations that help you actually remember what you study."
    },
    {
      question: "I failed my first test. Can MaplePass help me pass?",
      answer: "Yes! Many of our users came to us after failing their first attempt. Our weak area detection identifies exactly where you struggled, and our practice tests ensure you're fully prepared for your next attempt."
    },
    {
      question: "Are the questions similar to the real test?",
      answer: "Our questions are meticulously collated from a wide range of sources, including public library materials and official government publications. We also incorporate feedback from users to ensure our bank stays current with what's being asked in the actual test. Users consistently tell us that the questions on their real test were very similar to what they practiced on MaplePass."
    },
    {
      question: "What's included in the Pass Guarantee?",
      answer: "If you complete all practice tests, simulations, and mock exams on MaplePass but still don't pass your citizenship test, we'll refund your payment in full. We're that confident in our program."
    }
  ];

  return (
    <>
      <SEO
        title="Canadian Citizenship Test Prep — Free Practice Questions & Mock Exams"
        description="Prepare for your Canadian Citizenship Test with 500+ practice questions based on Discover Canada, realistic mock exams simulating the official IRCC test, 200+ flashcards, and study guides. Free tier available. Start studying today."
        keywords="Canadian citizenship test, citizenship exam prep, Discover Canada, citizenship test practice, Canadian citizenship questions, citizenship mock test, Canada citizenship 2026, citizenship test study guide, free citizenship practice test, IRCC test preparation, Canadian civics test"
        canonical="https://canadatestprep.ca/"
        faqData={faqs}
        breadcrumbs={[
          { name: "Home", url: "/" }
        ]}
      />
      <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-maple rounded-lg flex items-center justify-center">
                <span className="text-white font-heading font-bold text-xl">C</span>
              </div>
              <div className="flex flex-col">
                <span className="font-heading font-bold text-xl text-slate-900 leading-tight">CanadaTestPrep</span>
                <span className="text-[10px] text-slate-500 leading-tight">powered by MaplePass</span>
              </div>
            </Link>
            
            <div className="hidden md:flex items-center gap-8">
              <a href="#how-it-works" className="text-slate-600 hover:text-maple transition-colors">{t("landing.howItWorks") || "How It Works"}</a>
              {/* Features Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-1 text-slate-600 hover:text-maple transition-colors">
                    {t("landing.features") || "Features"}
                    <ChevronDown className="w-3 h-3" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  <DropdownMenuItem asChild className="cursor-pointer">
                    <Link to="/features/chapter-practice" className="flex items-center gap-2">
                      <BookOpen className="w-4 h-4 text-maple" />
                      Chapter Practice
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="cursor-pointer">
                    <Link to="/features/simulations" className="flex items-center gap-2">
                      <Zap className="w-4 h-4 text-truenorth" />
                      Test Simulations
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="cursor-pointer">
                    <Link to="/features/mock-tests" className="flex items-center gap-2">
                      <Trophy className="w-4 h-4 text-yellow-500" />
                      Mock Tests
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="cursor-pointer">
                    <Link to="/features/flashcards" className="flex items-center gap-2">
                      <Layers className="w-4 h-4 text-purple-500" />
                      Flashcards
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="cursor-pointer">
                    <Link to="/features/analytics" className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-emerald-500" />
                      Analytics & Insights
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <a href="#pricing" className="text-slate-600 hover:text-maple transition-colors">{t("landing.pricing") || "Pricing"}</a>
            </div>

            <div className="flex items-center gap-3">
              {/* Language Toggle */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="flex items-center gap-1.5 text-slate-600" data-testid="language-toggle">
                    <Globe className="w-4 h-4" />
                    <span className="hidden sm:inline">{language === "fr" ? "FR" : "EN"}</span>
                    <ChevronDown className="w-3 h-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setLanguage("en")} className="cursor-pointer">
                    <span className="mr-2">🇬🇧</span>
                    English
                    {language === "en" && <Check className="w-4 h-4 ml-auto" />}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setLanguage("fr")} className="cursor-pointer">
                    <span className="mr-2">🇫🇷</span>
                    Français
                    {language === "fr" && <Check className="w-4 h-4 ml-auto" />}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {auth.user ? (
                <Link to="/dashboard">
                  <Button className="bg-maple hover:bg-maple-600 text-white rounded-full px-6" data-testid="dashboard-btn">
                    {t("landing.navDashboard") || "Dashboard"}
                  </Button>
                </Link>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="ghost" className="text-slate-700" data-testid="login-btn">
                      {t("landing.navLogIn") || "Log in"}
                    </Button>
                  </Link>
                  <Link to="/register">
                    <Button className="bg-maple hover:bg-maple-600 text-white rounded-full px-6" data-testid="get-started-btn">
                      {t("landing.getStarted") || "Get Started"}
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-slate-50 to-white py-16 lg:py-24">
        {/* Rating badge */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 text-sm text-slate-600">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <span className="font-medium">{t("landing.excellent") || "Excellent"}</span>
              <span className="text-slate-400">|</span>
              <span>Based on reviews of real test takers</span>
            </div>
          </div>

          <div className="text-center max-w-4xl mx-auto">
            <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-slate-900 leading-tight mb-6">
              {t("landing.heroTitle") || "Pass your Canadian Citizenship Test on the first try."}{" "}
              <span className="text-maple">{t("landing.heroSubtitle") || "Guaranteed."}</span>
            </h1>
            
            <p className="text-lg lg:text-xl text-slate-600 mb-8 max-w-3xl mx-auto">
              {t("landing.heroDescription") || "Your complete toolkit for success: Prepare with 500+ test questions, simulation tests, chapter practice, flashcards and all the important tools you need."}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Link to="/register">
                <Button size="lg" className="bg-maple hover:bg-maple-600 text-white rounded-full px-10 h-14 text-lg w-full sm:w-auto" data-testid="hero-cta-btn">
                  {t("landing.unlockQuestions") || "Unlock all 500+ questions"}
                  <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>

            <p className="text-slate-600 mb-6">
              Join the fastest-growing Canadian citizenship prep community. <strong>Designed for people who don't leave results to chance.</strong>
            </p>

            {/* Social Proof Badge */}
            <div className="flex items-center justify-center gap-2 mb-8">
              <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-full px-4 py-2 text-sm">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </span>
                <span className="text-green-700 font-medium">New learners join every day.</span>
                <span className="text-green-600">Start today.</span>
              </div>
            </div>

            {/* Key benefits */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 max-w-4xl mx-auto text-sm">
              {[
                t("landing.keyBenefits.questions") || "Over 500+ questions seen on exam",
                t("landing.keyBenefits.studyGuide") || "Official Study Guide & Lessons",
                t("landing.keyBenefits.simulator") || "Real Test Simulator (Gov't format)",
                t("landing.keyBenefits.aiTools") || "Smart study tools & weak area detection",
                t("landing.keyBenefits.guarantee") || "Pass Guarantee: pass, or it's on us"
              ].map((benefit, idx) => (
                <div key={idx} className="flex items-center gap-2 justify-center bg-slate-50 rounded-full px-4 py-2">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span className="text-slate-700">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-slate-900 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="font-heading font-bold text-3xl md:text-4xl text-white mb-1">{stat.number}</p>
                <p className="text-slate-400">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Section - Book vs CanadaTestPrep */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-maple font-medium mb-2">{t("landing.comparison.nervousTitle") || "Nervous about your upcoming exam?"}</p>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-slate-900 mb-4">
              {t("landing.comparison.funWay") || "Say hello to a fun and intuitive way to prepare"}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t("landing.comparison.funDescription") || "It's 2026: why settle for outdated methods? We've packed all the tools into an engaging program, engineered to build your confidence one question at a time."}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Old Way - Book */}
            <Card className="border-2 border-slate-200 relative overflow-hidden">
              <div className="absolute top-4 right-4">
                <Badge variant="secondary" className="bg-red-100 text-red-700">{t("landing.comparison.highFailure") || "High Failure Rate"}</Badge>
              </div>
              <CardContent className="p-8">
                <h3 className="font-heading font-bold text-xl text-slate-700 mb-4">{t("landing.comparison.oldWay") || "Stand-alone Book"}</h3>
                {/* Old school book illustration */}
                <div className="w-full h-44 bg-gradient-to-b from-slate-100 to-slate-200 rounded-lg mx-auto mb-6 flex flex-col items-center justify-center relative overflow-hidden">
                  <div className="absolute inset-0 opacity-10">
                    {[...Array(12)].map((_, i) => (
                      <div key={i} className="h-3 bg-slate-300 my-1 mx-4 rounded" style={{ width: `${60 + Math.random() * 30}%` }} />
                    ))}
                  </div>
                  <div className="relative z-10 flex flex-col items-center">
                    <div className="w-20 h-28 bg-gradient-to-br from-amber-700 to-amber-900 rounded shadow-lg flex items-center justify-center border-l-4 border-amber-600">
                      <div className="text-amber-200 text-center">
                        <BookOpen className="w-8 h-8 mx-auto mb-1" />
                        <span className="text-xs font-serif">Discover<br/>Canada</span>
                      </div>
                    </div>
                    <span className="text-slate-500 text-xs mt-2">Dense 68-page booklet</span>
                  </div>
                </div>
                <ul className="space-y-3">
                  {comparisonFeatures.map((item, idx) => (
                    <li key={idx} className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">{item.feature}</span>
                      <span className="text-slate-400 flex items-center gap-1">
                        <XCircle className="w-4 h-4 text-red-400" />
                        {item.book}
                      </span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* New Way - MaplePass */}
            <Card className="border-2 border-green-300 relative overflow-hidden bg-gradient-to-br from-green-50 to-white">
              <div className="absolute top-4 right-4">
                <Badge className="bg-green-500 text-white">{t("landing.comparison.passRateBadge") || "High Pass Rate"}</Badge>
              </div>
              <CardContent className="p-8">
                <h3 className="font-heading font-bold text-xl text-slate-900 mb-4">{t("landing.comparison.newWay") || "CanadaTestPrep Premium"}</h3>
                {/* Modern app illustration */}
                <div className="w-full h-44 bg-gradient-to-br from-maple-50 to-truenorth-50 rounded-lg mx-auto mb-6 flex items-center justify-center relative overflow-hidden">
                  <div className="absolute inset-0 opacity-20">
                    <div className="absolute top-2 left-2 w-16 h-16 bg-green-200 rounded-full blur-xl" />
                    <div className="absolute bottom-2 right-2 w-20 h-20 bg-maple-200 rounded-full blur-xl" />
                  </div>
                  <div className="relative z-10 flex items-center gap-3">
                    {/* Phone mockup */}
                    <div className="w-24 h-36 bg-white rounded-2xl shadow-xl border border-slate-200 p-2">
                      <div className="w-full h-full bg-slate-50 rounded-xl flex flex-col items-center justify-center">
                        <div className="w-8 h-8 bg-maple rounded-lg flex items-center justify-center mb-1">
                          <span className="text-white font-bold text-sm">M</span>
                        </div>
                        <div className="space-y-1 w-full px-2">
                          <div className="h-1.5 bg-green-400 rounded-full w-3/4" />
                          <div className="h-1.5 bg-slate-200 rounded-full" />
                          <div className="h-1.5 bg-slate-200 rounded-full w-1/2" />
                        </div>
                        <div className="mt-2 flex gap-1">
                          <div className="w-4 h-4 bg-maple/20 rounded" />
                          <div className="w-4 h-4 bg-green-500/20 rounded" />
                          <div className="w-4 h-4 bg-truenorth/20 rounded" />
                        </div>
                      </div>
                    </div>
                    <div className="text-left">
                      <div className="flex items-center gap-1 mb-1">
                        <Trophy className="w-4 h-4 text-yellow-500" />
                        <span className="text-xs font-medium text-slate-700">Pass Ready</span>
                      </div>
                      <div className="flex items-center gap-1 mb-1">
                        <Target className="w-4 h-4 text-maple" />
                        <span className="text-xs font-medium text-slate-700">500+ Qs</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Zap className="w-4 h-4 text-truenorth" />
                        <span className="text-xs font-medium text-slate-700">33 Tests</span>
                      </div>
                    </div>
                  </div>
                </div>
                <ul className="space-y-3">
                  {comparisonFeatures.map((item, idx) => (
                    <li key={idx} className="flex items-center justify-between text-sm">
                      <span className="text-slate-700">{item.feature}</span>
                      <span className="text-green-600 font-medium flex items-center gap-1">
                        <CheckCircle2 className="w-4 h-4" />
                        {item.mapplepass}
                      </span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Pass Guarantee Section */}
      <section className="py-12 bg-green-50 border-y border-green-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-center gap-6 text-center md:text-left">
            <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="w-10 h-10 text-white" />
            </div>
            <div>
              <h3 className="font-heading font-bold text-2xl text-slate-900 mb-2">
                {t("landing.guarantee.title") || "Pass Guarantee: Pass your test, or your money back"}
              </h3>
              <p className="text-slate-600 max-w-2xl">
                {t("landing.guarantee.description") || "Even if you've failed before and taking tests makes your hands sweat, with CanadaTestPrep Premium, you're 100% guaranteed to pass. Complete our program and don't pass? We'll refund you in full."}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-maple font-medium mb-2">{language === "fr" ? "COMMENT ÇA MARCHE" : "HOW IT WORKS"}</p>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-slate-900 mb-4">
              {t("landing.howItWorksSection.title") || "Be fully prepared for your citizenship test"}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t("landing.howItWorksSection.description") || "Our course is fully online and designed to work with your busy schedule, not the other way around."}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {howItWorks.map((step, index) => (
              <div key={index} className="relative">
                <div className="text-center">
                  <div className="w-16 h-16 bg-maple-50 rounded-2xl flex items-center justify-center mx-auto mb-4 text-maple">
                    {step.icon}
                  </div>
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-2 w-8 h-8 bg-maple text-white rounded-full flex items-center justify-center font-bold text-sm">
                    {step.step}
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-slate-600">{step.description}</p>
                </div>
                {index < howItWorks.length - 1 && (
                  <div className="hidden lg:block absolute top-8 left-[60%] w-[80%] border-t-2 border-dashed border-slate-200" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 lg:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-maple font-medium mb-2">{language === "fr" ? "FONCTIONNALITÉS" : "FEATURES"}</p>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-slate-900 mb-4">
              {t("landing.featuresSection.title") || "Everything You Need to Pass"}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t("landing.featuresSection.description") || "Comprehensive tools designed to help you master the Canadian Citizenship Test"}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const CardWrapper = feature.link ? Link : 'div';
              const cardProps = feature.link ? { to: feature.link } : {};
              
              return (
                <CardWrapper key={index} {...cardProps} className={feature.link ? "block" : ""}>
                  <Card className={`border border-slate-200 bg-white hover:border-maple/50 hover:shadow-lg transition-all duration-300 group h-full ${feature.link ? 'cursor-pointer' : ''}`} data-testid={`feature-card-${index}`}>
                    <CardContent className="p-5">
                      <div className="w-11 h-11 bg-maple-50 rounded-xl flex items-center justify-center text-maple mb-4 group-hover:bg-maple group-hover:text-white transition-colors">
                        {feature.icon}
                      </div>
                      <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2">
                        {feature.title}
                      </h3>
                      <p className="text-sm text-slate-600">
                        {feature.description}
                      </p>
                      {feature.link && (
                        <p className="text-sm text-maple font-medium mt-3 flex items-center gap-1 group-hover:gap-2 transition-all">
                          Learn more <ChevronRight className="w-4 h-4" />
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </CardWrapper>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      {/* Pricing Section */}
      <section id="pricing" className="py-16 lg:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-maple font-medium mb-2">{t("landing.pricingSection.simplePricing") || "SIMPLE PRICING"}</p>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-slate-900 mb-4">
              {t("landing.pricingSection.title") || "Let us help you breeze through your test"}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t("landing.pricingSection.description") || "Simple pricing, no hidden fees. One-time purchase, not a subscription."}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Free Plan */}
            <Card className="border-2 border-slate-200">
              <CardContent className="p-6">
                <h3 className="font-heading font-bold text-xl text-slate-900 mb-2">Free Trial</h3>
                <p className="text-sm text-slate-600 mb-4">Get a taste of what CanadaTestPrep offers</p>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-slate-900">$0</span>
                </div>
                <Link to="/register">
                  <Button variant="outline" className="w-full mb-6">Start Free</Button>
                </Link>
                <ul className="space-y-3 text-sm">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span>1 Easy Simulation Test</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span>10 Sample Flashcards</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span>Study Guide Access</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span>Journey Tracker</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Pro Plan */}
            <Card className="border-2 border-maple relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <Badge className="bg-maple text-white px-4">SALE - 33% OFF</Badge>
              </div>
              <CardContent className="p-6">
                <h3 className="font-heading font-bold text-xl text-slate-900 mb-2">Pro</h3>
                <p className="text-sm text-slate-600 mb-4">7-day full access pass</p>
                <div className="mb-6">
                  <span className="text-lg text-slate-400 line-through mr-2">$14.99</span>
                  <span className="text-4xl font-bold text-slate-900">$9.99</span>
                  <span className="text-slate-600">/week</span>
                </div>
                <Link to="/pricing">
                  <Button className="w-full bg-maple hover:bg-maple-600 text-white mb-6">Get Pro Access</Button>
                </Link>
                <ul className="space-y-3 text-sm">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span><strong>All 500+ Questions</strong></span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span>30 Simulation Tests</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span>3 Mock Tests</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span>200+ Flashcards</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span>Weak Area Detection</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Premium Plan */}
            <Card className="border-2 border-yellow-400 relative bg-gradient-to-br from-yellow-50 to-white">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <Badge className="bg-yellow-500 text-white px-4">BEST VALUE - 50% OFF</Badge>
              </div>
              <CardContent className="p-6">
                <h3 className="font-heading font-bold text-xl text-slate-900 mb-2">Premium</h3>
                <p className="text-sm text-slate-600 mb-4">Lifetime access forever</p>
                <div className="mb-6">
                  <span className="text-lg text-slate-400 line-through mr-2">$29.99</span>
                  <span className="text-4xl font-bold text-slate-900">$14.99</span>
                  <span className="text-slate-600">/lifetime</span>
                </div>
                <Link to="/pricing">
                  <Button className="w-full bg-yellow-500 hover:bg-yellow-600 text-white mb-6">Get Lifetime Access</Button>
                </Link>
                <ul className="space-y-3 text-sm">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span><strong>Everything in Pro</strong></span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span><strong>Lifetime Access</strong></span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span>Priority Support</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span>Future Updates Free</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-green-500" />
                    <span><strong>Pass Guarantee</strong></span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-slate-900 mb-4">
              {t("landing.faq") || "Frequently Asked Questions"}
            </h2>
          </div>

          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <Card key={index} className="border border-slate-200">
                <CardContent className="p-6">
                  <h3 className="font-heading font-semibold text-lg text-slate-900 mb-3">
                    {faq.question}
                  </h3>
                  <p className="text-slate-600">
                    {faq.answer}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 lg:py-24 bg-maple">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <ShieldCheck className="w-10 h-10 text-white" />
          </div>
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-6">
            {t("landing.ctaSection.title") || "When you absolutely need to pass your citizenship test"}
          </h2>
          <p className="text-xl text-white/90 mb-4 max-w-2xl mx-auto">
            {t("landing.ctaSection.description") || "You have zero time and a million responsibilities. If you're going to study, you need to be sure you'll pass on the first try."}
          </p>
          <p className="text-white/80 mb-8">
            <strong>{t("landing.ctaSection.soYouCanGetBack") || "So you can get back to your life."}</strong>
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button size="lg" className="bg-white text-maple hover:bg-slate-100 rounded-full px-10 h-14 text-lg font-semibold" data-testid="cta-start-btn">
                {t("landing.unlockQuestions") || "Unlock all 500+ questions"}
                <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
          <p className="text-white/70 mt-6 text-sm">
            Join the fastest-growing Canadian citizenship prep community. <strong>Designed for people who don't leave results to chance.</strong>
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-12 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            {/* Brand */}
            <div className="md:col-span-1">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-maple rounded-lg flex items-center justify-center">
                  <span className="text-white font-heading font-bold text-xl">C</span>
                </div>
                <div className="flex flex-col">
                  <span className="font-heading font-bold text-lg text-white leading-tight">CanadaTestPrep.ca</span>
                  <span className="text-xs text-slate-400 leading-tight">powered by MaplePass</span>
                </div>
              </div>
              <p className="text-sm text-slate-400">
                The #1 Canadian Citizenship Test preparation platform. Pass your exam with confidence.
              </p>
            </div>
            
            {/* Features Links */}
            <div>
              <h4 className="text-white font-semibold mb-4">Features</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li>
                  <Link to="/features/chapter-practice" className="hover:text-white transition-colors">
                    Chapter Practice
                  </Link>
                </li>
                <li>
                  <Link to="/features/simulations" className="hover:text-white transition-colors">
                    Test Simulations
                  </Link>
                </li>
                <li>
                  <Link to="/features/mock-tests" className="hover:text-white transition-colors">
                    Mock Tests
                  </Link>
                </li>
                <li>
                  <Link to="/features/flashcards" className="hover:text-white transition-colors">
                    Flashcards
                  </Link>
                </li>
                <li>
                  <Link to="/features/analytics" className="hover:text-white transition-colors">
                    Analytics & Insights
                  </Link>
                </li>
              </ul>
            </div>
            
            {/* Company Links */}
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li>
                  <Link to="/pricing" className="hover:text-white transition-colors">
                    Pricing
                  </Link>
                </li>
                <li>
                  {auth.user ? (
                    <Link to="/contact" className="hover:text-white transition-colors">
                      Contact Us
                    </Link>
                  ) : (
                    <Link to="/register" className="hover:text-white transition-colors">
                      Contact Us
                    </Link>
                  )}
                </li>
              </ul>
            </div>
            
            {/* Legal Links */}
            <div>
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li>
                  <Link to="/privacy" className="hover:text-white transition-colors">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="hover:text-white transition-colors">
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link to="/disclaimer" className="hover:text-white transition-colors">
                    Disclaimer
                  </Link>
                </li>
                <li>
                  <a href="/api/sitemap.xml" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                    Sitemap
                  </a>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-800 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-sm">
              © 2026 CanadaTestPrep.ca. {t("landing.footer.madeWithLove") || "Made with love in Canada"}
            </p>
            <div className="flex items-center gap-4">
              <Link to="/privacy" className="text-slate-500 text-sm hover:text-slate-400 transition-colors">
                Privacy
              </Link>
              <Link to="/terms" className="text-slate-500 text-sm hover:text-slate-400 transition-colors">
                Terms
              </Link>
              <Link to="/disclaimer" className="text-slate-500 text-sm hover:text-slate-400 transition-colors">
                Disclaimer
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default LandingPage;




